import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AllEntities, BaseEntity } from 'c-sdk';
import { getBaseSliceSelectors, getBaseSliceThunks } from '../Data/EntitySchemaActions';
import { hookUtilGetById } from './utils';
import type { ByIdNetworkRequests } from '../Types/network';
import { NetworkRequestState } from '../Types/network';

// An entity with no registered slice (some EntityAutocomplete entities have none) reads these.
// One shared object each, never a new one per call: a selector that returns a fresh object makes
// react-redux warn and re-render on every store change.
const EMPTY_LOADING_BY_ID: ByIdNetworkRequests = {};
const EMPTY_KEYED_ENTITIES: Record<number, any> = {};
const emptyLoadingById = () => EMPTY_LOADING_BY_ID;
const emptyKeyedEntities = () => EMPTY_KEYED_ENTITIES;

const useEntityData = <RootState, Entity extends BaseEntity>(entityName: keyof AllEntities) => {
    const dispatch = useDispatch();
    const thunks = useMemo(() => getBaseSliceThunks<RootState, Entity>(entityName), [entityName]);
    const sliceSelectors = useMemo(() => getBaseSliceSelectors<Entity>(entityName), [entityName]);

    const loadingById = useSelector(sliceSelectors?.loadingByIdMap ?? emptyLoadingById);
    const getLoadingById = useCallback(
        (id: number) => loadingById[id] ?? { state: NetworkRequestState.Idle },
        [loadingById],
    );
    const upsertEntity = useCallback(
        (entity: Entity) => dispatch(thunks.upsertEntity(entity)),
        [dispatch, thunks],
    );

    const upsertEntities = useCallback(
        (entities: Entity[]) => dispatch(thunks.upsertEntities(entities)),
        [dispatch, thunks],
    );

    const keyedEntities = useSelector(
        sliceSelectors?.selectEntities ?? emptyKeyedEntities,
    ) as Record<number, Entity>;

    const getById = useCallback(
        ({
            id,
            forceRefresh = false,
            includes,
        }: {
            id: number | null | undefined;
            forceRefresh?: boolean;
            includes?: string[];
        }) => {
            if (id == null) {
                return null;
            }
            return hookUtilGetById<RootState, Entity>(thunks.getById)(
                id,
                forceRefresh ? {} : keyedEntities,
                getLoadingById,
                dispatch,
                includes,
            ) as Entity | undefined | null;
        },
        [keyedEntities, thunks, dispatch, getLoadingById],
    );
    const resetLoadingByIdState = useCallback(
        (id: number) => dispatch(thunks.resetLoadingByIdState(id)),
        [thunks, dispatch],
    );

    return useMemo(
        () => ({
            keyedEntities,

            getById,
            resetLoadingByIdState,
            loadingById,
            getLoadingById,

            upsertEntity,
            upsertEntities,
        }),
        [
            keyedEntities,
            getById,
            resetLoadingByIdState,
            loadingById,
            getLoadingById,
            upsertEntity,
            upsertEntities,
        ],
    );
};

export default useEntityData;
